import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { TableCell, TableRow, SpeedDialIcon, SpeedDialAction, Box } from '@mui/material';
import { Autocomplete, IconButton } from '@totalsoft/rocket-ui';
import { Radio } from '@mui/material';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { FinancialProductActionDial } from '../../../../list/components/Styles';
import { EditNote, Subject, MoreHoriz } from '@mui/icons-material';
import Close from '@mui/icons-material/Close';
import FinancialProductSeasonalityTemplateModalComponent from './FinancialProductSeasonalityTemplateModalComponent';
import { seasonalityTemplateModalVar } from './variables';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';

const FinancialProductSeasonalityTemplatesTableRow = ({
  canWrite,
  seasonalityTemplate,
  index,
  onEdit,
  onChangeData,
  onCloseEdit,
  onSave,
  onDelete,
  lockControls,
  financialProductSeasonalityTemplatesDictionary,
  financialProductSeasonalityTemplatesValidation
}) => {
  const [t] = useTranslation();
  const historyContext = useHistoryWithContext();

  const onRedirect = useCallback(
    id => {
      historyContext.push(`/scheduleTypeCellParamTemplates/${id}`);
    },
    [historyContext]
  );

  return (
    <TableRow>
      <FinancialProductSeasonalityTemplateModalComponent />
      {seasonalityTemplate.isInEditing ? (
        <>
          <TableCell>
            <Autocomplete
              valueKey={'templateId'}
              labelKey={'templateName'}
              value={{
                templateName: financialProductSeasonalityTemplatesDictionary?.find(val => val.templateId === seasonalityTemplate.templateId)?.templateName,
                templateId: seasonalityTemplate.templateId
              }}
              options={financialProductSeasonalityTemplatesDictionary}
              onChange={event => {
                onChangeData(index, 'templateId', event.templateId);
              }}
              error={!isValid(financialProductSeasonalityTemplatesValidation[index].templateId)}
              helperText={getErrors(financialProductSeasonalityTemplatesValidation[index].templateId)}
            />
          </TableCell>
          <TableCell>
            <Radio
              checked={seasonalityTemplate.default}
              onClick={() => {
                onChangeData(index, 'default', !seasonalityTemplate.default);
              }}
              error={!isValid(financialProductSeasonalityTemplatesValidation[index].default)}
              helperText={getErrors(financialProductSeasonalityTemplatesValidation[index].default)}
            />
          </TableCell>
          <TableCell align='right'>
          <Box display='flex'>
          <Box position='relative' flexGrow={1} mr={0.5}>
            <FinancialProductActionDial
              ariaLabel={`${seasonalityTemplate.templateId}-actions-dial`}
              icon={<SpeedDialIcon icon={<MoreHoriz />} openIcon={<Close />} />}
              direction={'left'}
              transitionDuration={0}
              FabProps={{ size: 'medium' }}
            >
              <SpeedDialAction
                tooltipTitle={t('FinancialProduct.Details.SeasonalityTemplates.ViewTemplate')}
                icon={<Subject />}
                onClick={() => seasonalityTemplateModalVar({ open: true, templateId : seasonalityTemplate.templateId})}
                disabled={!canWrite}
              />
              <SpeedDialAction
                tooltipTitle={t('FinancialProduct.Details.SeasonalityTemplates.EditTemplate')}
                icon={<EditNote />}
                onClick={() => onRedirect(seasonalityTemplate.templateId)}
              />
            </FinancialProductActionDial>
          </Box>
            <IconButton
              type='save'
              fontSize='medium'
              color='transparent'
              tooltip={t('FinancialProduct.Save')}
              disabled={!canWrite || lockControls}
              onClick={() => onSave(index)}
            />
            <IconButton
              type='cancel'
              fontSize='medium'
              color='transparent'
              tooltip={t('FinancialProduct.CloseEdit')}
              onClick={() => onCloseEdit(seasonalityTemplate.seasonalityTemplateId, index)}
            />
            </Box>
          </TableCell>
        </>
      ) : (
        <>
          <TableCell>
            {financialProductSeasonalityTemplatesDictionary?.find(val => val.templateId === seasonalityTemplate.templateId)?.templateName}
          </TableCell>
          <TableCell>
            <Radio checked={seasonalityTemplate.default} disabled={true} />
          </TableCell>
          <TableCell align='right'>
          <Box display='flex'>
          <Box position='relative' flexGrow={1} mr={0.5}>
            <FinancialProductActionDial
              ariaLabel={`${seasonalityTemplate.templateId}-actions-dial`}
              icon={<SpeedDialIcon icon={<MoreHoriz />} openIcon={<Close />} />}
              direction={'left'}
              transitionDuration={0}
              FabProps={{ size: 'medium' }}
            >
              <SpeedDialAction
                tooltipTitle={t('FinancialProduct.Details.SeasonalityTemplates.ViewTemplate')}
                icon={<Subject />}
                onClick={() => seasonalityTemplateModalVar({ open: true, templateId : seasonalityTemplate.templateId})}
                disabled={!canWrite}
              />
              <SpeedDialAction
                tooltipTitle={t('FinancialProduct.Details.SeasonalityTemplates.EditTemplate')}
                icon={<EditNote />}
                onClick={() => onRedirect(seasonalityTemplate.templateId)}
              />
            </FinancialProductActionDial>
          </Box>
            <IconButton
              type='edit'
              fontSize='medium'
              color='transparent'
              tooltip={t('FinancialProduct.Edit')}
              onClick={() => onEdit(index)}
            />
            <IconButton
              type='delete'
              fontSize='medium'
              color='transparent'
              tooltip={t('FinancialProduct.Delete')}
              onClick={() => onDelete(seasonalityTemplate.seasonalityTemplateId, index)}
              disabled={!canWrite || lockControls}
            />
            </Box>
          </TableCell>
        </>
      )}
    </TableRow>
  );
};

FinancialProductSeasonalityTemplatesTableRow.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  seasonalityTemplate: PropTypes.object,
  index: PropTypes.number,
  onEdit: PropTypes.func.isRequired,
  onCloseEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onChangeData: PropTypes.func.isRequired,
  lockControls: PropTypes.bool.isRequired,
  financialProductSeasonalityTemplatesDictionary: PropTypes.array.isRequired,
  financialProductSeasonalityTemplatesValidation: PropTypes.object.isRequired
};

export default FinancialProductSeasonalityTemplatesTableRow;
